import { DrawerCard, DrawerTitle } from "@mixitone/components";
import CourtIcon from "@mixitone/icons/court.svg";
import { ApplicationView } from "@mixitone/mvc";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import GamesOverviewController from "components/Pages/Tournament/TournamentGames/GamesOverviewController";
import { injectModal } from "lib/injectParams";

interface Props {
  id: string;
}

const ProgressBar = ({ percent }: { percent: number }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
    <div
      className="h-full rounded-full bg-brand-600 transition-[width]"
      style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
    />
  </div>
);

const GamesOverviewDrawer = ApplicationView<Props>(({ id }: Props) => {
  const controller = GamesOverviewController.use();
  const { loading, overview } = controller.state;

  const contextLink = <DrawerContextLink context="tournamentGames" params={{ id }} />;

  if (loading) {
    return (
      <RoutedDrawer spinner>
        <DrawerTitle icon={<CourtIcon className="mr-2 w-[24px] fill-gray-400" />} contextLink={contextLink}>
          Tournament overview
        </DrawerTitle>
      </RoutedDrawer>
    );
  }

  return (
    <RoutedDrawer>
      <DrawerTitle icon={<CourtIcon className="mr-2 w-[24px] fill-gray-400" />} contextLink={contextLink}>
        Tournament overview
      </DrawerTitle>

      <div className="space-y-4" data-testid="tournament-games-overview-drawer">
        <DrawerCard title="Summary">
          <div className="mb-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-gray-500">Resolved</div>
              <div className="text-lg font-medium tabular-nums">
                {overview.scored + overview.voided}/{overview.total} ({overview.completionPercent}%)
              </div>
              {overview.voided > 0 && (
                <div className="text-xs text-gray-500 tabular-nums">
                  {overview.scored} scored · {overview.voided} voided
                </div>
              )}
            </div>
            <div>
              <div className="text-gray-500">Remaining</div>
              <div className="text-lg font-medium tabular-nums">{overview.remaining}</div>
            </div>
            <div>
              <div className="text-gray-500">In progress</div>
              <div className="text-lg font-medium tabular-nums">{overview.inProgress}</div>
            </div>
            <div>
              <div className="text-gray-500">Awaiting score</div>
              <div className="text-lg font-medium tabular-nums">{overview.awaitingScore}</div>
            </div>
          </div>
          <ProgressBar percent={overview.completionPercent} />
        </DrawerCard>

        {overview.competitionDetails.map((competition) => {
          const completed = competition.scored + competition.voided;
          return (
            <DrawerCard
              key={competition.competitionId}
              title={controller.competitionName(competition.competitionId)}
            >
              <div className="mb-3 flex items-center justify-between gap-2 text-sm">
                <span className="tabular-nums text-gray-700">
                  {completed}/{competition.total} completed
                  {competition.voided > 0 ? ` · ${competition.voided} voided` : ""}
                </span>
                <span className="tabular-nums text-gray-500">{competition.remaining} remaining</span>
              </div>
              <ProgressBar percent={competition.completionPercent} />
              {competition.roundGroups.length > 0 && (
                <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                  {competition.roundGroups.map((roundGroup) => {
                    const roundCompleted = roundGroup.scored + roundGroup.voided;
                    return (
                      <div
                        key={roundGroup.key}
                        className="flex items-center justify-between gap-2 text-sm text-gray-700"
                      >
                        <span>{roundGroup.label}</span>
                        <span className="tabular-nums text-gray-500">
                          {roundCompleted}/{roundGroup.total}
                          {roundGroup.voided > 0 ? ` · ${roundGroup.voided} voided` : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </DrawerCard>
          );
        })}

        {overview.recentResults.length > 0 && (
          <DrawerCard title="Recent results">
            <div className="space-y-2 text-sm">
              {overview.recentResults.map((match) => {
                const winners = match.entries?.winners ?? [];
                const current = match.entries?.current?.filter(Boolean) ?? [];
                return (
                  <div key={match.id} className="rounded bg-gray-50 px-3 py-2">
                    <div className="font-medium text-gray-900">
                      Round {(match.round ?? 0) + 1}
                      {match.format_data?.group ? ` / ${match.format_data.group}` : ""}
                    </div>
                    <div className="text-xs text-gray-500">
                      {winners.length > 0
                        ? `Winner entry ${winners.join(", ")}`
                        : `${current.length} entries`}
                    </div>
                  </div>
                );
              })}
            </div>
          </DrawerCard>
        )}
      </div>
    </RoutedDrawer>
  );
});

export default injectModal(GamesOverviewController.scope(GamesOverviewDrawer));
